import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTheme } from '@forma/forma-ui-theme';
import PageBlock from '@/components/PageBlock';
import MetaTags from '@/components/MetaTags';
import MarketplaceItems from '@/components/views/marketplace/MarketplaceItems';
import useMetaTags from '@/hooks/useMetaTags';
import getHeadersFromServer from '@/helpers/getHeadersFromServer';

import mockApi from '@/api/mockApi';

import styles from './marketplace.module.css';

export const getServerSideProps: GetServerSideProps = async ({ req, res, params }) => {
  const locale = params!.locale as string;

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=14400, stale-while-revalidate=10',
  );
  const mainData = await mockApi.getMainData(locale);
  const pageData = await mockApi.getPageData(locale, 'marketplace');
  const headersParse = getHeadersFromServer(req);

  return {
    props: {
      initialState: {
        main: mainData,
      },
      pageData,
      headersParse,
      ...(await serverSideTranslations(locale, ['common', 'uikit'])),
    },
  };
};

interface MarketplaceItem {
  id: string,
  icon: string,
  title: string,
  description?: string,
  labels: string[],
  isNew?: boolean,
  isInstalled?: boolean,
}

interface MarketplaceBlock {
  title?: string,
  items: MarketplaceItem[],
}

interface MarketplaceLabel {
  id: string,
  name: string,
  color: string,
  backgroundColor: string
}

interface MarketplacePageData {
  labels: MarketplaceLabel[],
  new_m: MarketplaceBlock,
  signatures: MarketplaceBlock,
  crm: MarketplaceBlock,
  storage: MarketplaceBlock,
  settings: MarketplaceBlock,
  partnership: MarketplaceBlock,
}

const appendLabels = (items: MarketplaceItem[], labels: MarketplaceLabel[]) => {
  return items.map(item => ({
    ...item,
    labels: item.labels.reduce((acc: MarketplaceLabel[], id) => {
      const label = labels.find(label => label.id === id);
      return label ? [...acc, label] : acc;
    }, []),
  }));
};

const Page = ({ pageData: { labels, new_m, signatures, crm, storage, settings, partnership } }: { pageData: MarketplacePageData }) => {
  const { t } = useTranslation();

  const metaData = {
    title: t('meta_titles.sign'),
    description: t('meta_descriptions.sign'),
    keywords: t('main_keywords'),
  };
  const { lang } = useTheme();
  useMetaTags({ lang, ...metaData });

  return (
    <>
      <Head>
        <title>{metaData.title}</title>
        <MetaTags {...metaData} />
      </Head>

      <PageBlock
        title="Маркетплейс виджетов"
        className={styles.pageWrap}
      >
        <div>Подключайте дополнительные модули в DataDoc, чтобы ещё качественнее и быстрее решать задачи вашего бизнеса</div>

        <MarketplaceItems title={new_m.title} items={appendLabels(new_m.items, labels)} />
        <MarketplaceItems title={signatures.title} items={appendLabels(signatures.items, labels)} />
        <MarketplaceItems title={crm.title} items={appendLabels(crm.items, labels)} />
        <MarketplaceItems title={storage.title} items={appendLabels(storage.items, labels)} />
        <MarketplaceItems title={settings.title} items={appendLabels(settings.items, labels)} />
        <MarketplaceItems title={partnership.title} items={appendLabels(partnership.items, labels)} />
      </PageBlock>
    </>
  );
};

export default Page;
